import * as React from "react";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";


import { Button, Menu, MenuItem } from "@mui/material";

import { InputField } from "./InputField.tsx";
import { useConfig } from "../hooks/useConfig.ts";


import {
  setClient,
  setErrors,
  setSpendDetailsRows,
} from "../Store/roiStore.ts";

export const Row = ({ onChange, onAddRow, onRemoveRow, rowId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const config = useConfig();
  const spendRowsConfig = config.inputFields.spendDetailsRow;
  const spendDetailRows = useSelector(
    (state) => state.inputFields.spendDetailsRows
  );
  const isSameDayIncluded = useSelector(
    (state) =>
      !state.inputFields.sameDay?.isSameDayOnly &&
      (state.inputFields.client?.currentParSolutions ?? []).includes("PAR SameDay")
  );
  const rows = isSameDayIncluded
    ? spendRowsConfig
    : spendRowsConfig.filter((field) => field.label !== "Annual Bill Only Spend");
  const { dispatch } = useStore();
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const addRow = () => {
    if (!onAddRow) return;
    onAddRow(rowId);
    setAnchorEl(null);
  };

  const removeRow = () => {
    if (!onAddRow) return;
    onRemoveRow(rowId);
    setAnchorEl(null);
  };

  const getHelperText = (index, row, value, label) => {
    // if it hasn't changed, don't validate
    if (spendDetailRows?.[row]?.[label] === undefined) return;

    switch (index) {
      // "Avg. Order Days per Week (per location)" — guard against aggregate
      // values being entered (e.g. summed across all locations). The labor
      // savings formula multiplies this by the entire facility's scale count,
      // so a value > 7 produces wildly inflated savings.
      case 4:
        return parseFloat(value) > 7
          ? "Must be 7 or less (days per week, per location)"
          : false;

      default:
        return String(value ?? "").trim().length === 0 && String(value ?? "") !== "0"
          ? "Can't be zero or empty"
          : false;
    }
  };

  // Non-blocking advisory shown in red on the scales field. Scales are normally
  // fewer than SKUs, so flag when they exceed it — but never block the user
  // (the step's Next button doesn't depend on this), since edge cases exist.
  const getSoftNote = (index, row, value) => {
    if (index !== 2) return "";
    const skus = parseFloat(spendDetailRows?.[row]?.["Total SKUs"]);
    const scales = parseFloat(value);
    return Number.isFinite(skus) && Number.isFinite(scales) && scales > skus
      ? "Scales are usually fewer than SKUs — double-check this number."
      : "";
  };

  return (
    <div className="row">
      <div className="rowId">Facility {rowId}</div>
      <div className="subrow">
        {rows.map((field, index) => {
          const label = field.displayLabel ?? field.label;
          const fieldKey = field.label;
          const fieldValue =
            spendDetailRows?.["row" + rowId]?.[fieldKey] ??
            field.defaultValue ??
            (field.type === "number" ? 0 : "");
          const validationMessage = getHelperText(
            index,
            "row" + rowId,
            fieldValue,
            field.label
          );
          const softNote = getSoftNote(index, "row" + rowId, fieldValue);

          return (
            <div className="field-wrapper" key={index}>
              <div className="field-label">{label}</div>
              <InputField
                error={validationMessage || softNote}
                helperText={validationMessage || softNote || field.smallNote || ""}
                value={fieldValue}
                placeholder={field.placeholder}
                hiddenLabel
                onValueInputChange={(name, value) =>
                  dispatch(
                    setSpendDetailsRows({
                      rowId: rowId,
                      index: index,
                      name: fieldKey,
                      value: value,
                    })
                  )
                }
                {...(field.prefix && { prefix: field.prefix })}
                {...(field.suffix && { suffix: field.suffix })}
                {...(field.type && { type: field.type })}
                {...(field.defaultValue && {
                  defaultValue: field.defaultValue,
                })}
              />
            </div>
          );
        })}
      </div>
      <div className="rowActions">
        <Button
          id="basic-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
        >
          Actions
        </Button>
        <Menu
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={addRow}>Add Facility</MenuItem>
          <MenuItem onClick={removeRow}>Delete Facility</MenuItem>
        </Menu>
      </div>
    </div>
  );
};
